import type { GameData, DecodedToken } from "@/interfaces";
import gameConfig from "../../game_config.json";

/**
 * Type for game configuration data without the game_id field
 */
type GameConfigData = Omit<GameData, "game_id">;

/**
 * Gets game configuration by gameId (appId) from game_config.json
 */
export function getGameDataById(gameId: string): GameData | null {
  try {
    if (gameConfig[gameId as keyof typeof gameConfig]) {
      return {
        ...(gameConfig[gameId as keyof typeof gameConfig] as GameConfigData),
        game_id: gameId,
      };
    }
    return null;
  } catch (error) {
    console.error('Failed to get game data by gameId:', error);
    return null;
  }
}

/**
 * Gets the default game configuration (first game in config)
 */
export function getDefaultGameData(): GameData {
  try {
    const firstGameId = Object.keys(gameConfig)[0];
    return {
      ...(gameConfig[firstGameId as keyof typeof gameConfig] as GameConfigData),
      game_id: firstGameId,
    };
  } catch (error) {
    console.error('Failed to get default game data:', error);
    // Return a minimal fallback configuration
    return {
      game_id: 'unknown',
      checkout_title: 'Checkout',
      stripe_public_key: '',
    };
  }
}

/**
 * Gets game configuration with fallback logic
 * Priority: specific gameId -> default game config
 */
export function getGameDataWithFallback(gameId?: string | null): GameData {
  if (gameId) {
    const gameData = getGameDataById(gameId);
    if (gameData) {
      return gameData;
    }
  }
  
  return getDefaultGameData();
}

/**
 * Augments game data with customer portal URL if provided
 */
export function augmentGameDataWithPortalUrl(
  gameData: GameData,
  customerPortalUrl: string | null
): GameData {
  return customerPortalUrl && !gameData.customer_portal_url
    ? { ...gameData, customer_portal_url: customerPortalUrl }
    : gameData;
}

/**
 * Gets deep link with fallback logic
 * Priority: returnDeeplink from token -> game config deep_link -> default
 */
export function getDeepLinkWithFallback(
  gameId?: string, 
  tokenData?: DecodedToken
): string {
  // First priority: returnDeeplink from token
  if (tokenData?.returnDeeplink) {
    return tokenData.returnDeeplink;
  }
  
  // Second priority: deep_link from game config
  if (gameId) {
    const gameData = getGameDataById(gameId);
    if (gameData?.deep_link) {
      return gameData.deep_link;
    }
  }
  
  // Third priority: deep_link from default game config
  const defaultGameData = getDefaultGameData();
  return defaultGameData.deep_link || "itms-apps://itunes.apple.com";
}

/**
 * Gets fallback URL with fallback logic
 * Priority: game config fallback_url -> default fallback
 */
export function getFallbackUrlWithFallback(
  gameId?: string,
  tokenData?: DecodedToken
): string {
  // First priority: fallback_url from game config
  if (gameId) {
    const gameData = getGameDataById(gameId);
    if (gameData?.fallback_url) {
      return gameData.fallback_url;
    }
  }
  
  // Second priority: fallback_url from default game config
  const defaultGameData = getDefaultGameData();
  return defaultGameData.fallback_url || "https://github.com";
}

/**
 * Gets Stripe public key with fallback logic
 */
export function getStripePublicKeyWithFallback(gameId?: string): string {
  if (gameId) {
    const gameData = getGameDataById(gameId);
    if (gameData?.stripe_public_key) {
      return gameData.stripe_public_key;
    }
  }
  
  const defaultGameData = getDefaultGameData();
  return defaultGameData.stripe_public_key || '';
}

/**
 * Gets game theme data with fallback to default theme
 */
export function getGameTheme(gameId?: string, themeName: string = 'default'): any {
  const gameData = gameId ? getGameDataById(gameId) : getDefaultGameData();
  
  if (gameData?.themes?.[themeName]) {
    return gameData.themes[themeName];
  }
  
  // Fallback to default theme
  if (gameData?.themes?.default) {
    return gameData.themes.default;
  }
  
  return null;
}

/**
 * Gets PayPal payment method type from game configuration
 * Returns "one-time" by default if not specified
 */
export function getPayPalPaymentMethodType(gameId?: string): "one-time" | "vaulted" {
  const gameData = gameId ? getGameDataById(gameId) : getDefaultGameData();
  return gameData?.payment_methods?.paypal || "one-time";
}

/**
 * Checks if PayPal vaulted payments are enabled for the game
 */
export function isPayPalVaultedEnabled(gameId?: string): boolean {
  return getPayPalPaymentMethodType(gameId) === "vaulted";
}

/**
 * Checks if PayPal is enabled for the game (either one-time or vaulted)
 */
export function isPayPalEnabled(gameId?: string): boolean {
  const gameData = gameId ? getGameDataById(gameId) : getDefaultGameData();
  return gameData?.paypal_enabled === true || !!gameData?.payment_methods?.paypal;
}

// Legacy compatibility functions - will be deprecated
/**
 * @deprecated Use getGameDataById instead
 */
export function getGameDataByAppId(appId: string): GameData | null {
  return getGameDataById(appId);
}

/**
 * Determines if background image should be enabled based on hierarchy:
 * 1. URL query parameter (?bg=enabled|disabled)
 * 2. Game config bg field ("enabled"|"disabled")
 * 3. Default to enabled
 * 
 * @param bgQueryParam - Value from ?bg parameter (e.g., "enabled" or "disabled")
 * @param gameData - Game configuration data
 * @returns true if background should be enabled, false otherwise
 */
export function isBackgroundEnabled(
  bgQueryParam: string | null,
  gameData: GameData
): boolean {
  // Priority 1: URL query parameter
  if (bgQueryParam !== null) {
    return bgQueryParam === 'enabled';
  }
  
  // Priority 2: Game config bg field
  if (gameData.bg !== undefined) {
    return gameData.bg === 'enabled';
  }
  
  // Priority 3: Default to enabled
  return true;
}

/**
 * @deprecated Use getGameDataWithFallback instead
 */
export function getGameConfiguration(gameId: string | null): GameData {
  return getGameDataWithFallback(gameId);
}
